import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, FileText, Calendar, Users, Eye } from 'lucide-react';
import axios from 'axios';

const Dashboard = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/offers/`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        // Force fresh data by adding timestamp
        params: { _t: new Date().getTime() }
      });
      console.log('[Dashboard] API response - total offers:', response.data.length);
      console.log('[Dashboard] Offers:', response.data.map(o => o.clientName));
      setOffers(response.data);
    } catch (err) {
      setError('Nepodařilo se načíst nabídky');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const handleOfferClick = (offer) => {
    // If offer has customUrl, use it directly; otherwise use default route
    if (offer.customUrl) {
      navigate(offer.customUrl);
    } else {
      navigate(`/nabidky/${offer.slug}`);
    }
  };

  const handleStatusChange = async (slug, newStatus, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/offers/${slug}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh offers
      fetchOffers();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'prijata': return 'bg-green-500/20 text-green-300 border-green-500/50';
      case 'odmitnuta': return 'bg-red-500/20 text-red-300 border-red-500/50';
      default: return 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'prijata': return 'Přijatá';
      case 'odmitnuta': return 'Odmítnutá';
      default: return 'Probíhající';
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <header className="bg-[#2a2a2a] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/4t7rchw6_VPA-logo-white.png" 
                alt="VŠE PRO AKCI" 
                className="h-10"
              />
              <div className="border-l border-gray-700 pl-4">
                <p className="text-gray-400 text-sm">Databáze nabídek</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 border border-purple-500 rounded-lg text-white transition-all"
              >
                <span>⚙️ Admin</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-300 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span>Odhlásit se</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Přehled nabídek</h2>
          <p className="text-gray-400">Klikněte na nabídku pro zobrazení detailu</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
            <p className="text-gray-400 mt-4">Načítám nabídky...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && offers.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-2">Zatím žádné nabídky</p>
            <p className="text-gray-500">Nabídky se zobrazí po jejich vytvoření v databázi</p>
          </div>
        )}

        {/* Offers Grid */}
        {!loading && offers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <div
                key={offer.slug}
                onClick={() => handleOfferClick(offer)}
                className="bg-[#2a2a2a] rounded-xl p-6 border border-gray-800 hover:border-cyan-500 transition-all cursor-pointer transform hover:scale-105 hover:shadow-2xl"
              >
                {/* Client Logo/Name */}
                <div className="flex items-center justify-between mb-4">
                  <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                    {offer.clientLogo ? (
                      <img src={offer.clientLogo} alt={offer.clientName} className="w-full h-full object-contain p-2" />
                    ) : (
                      <span className="text-2xl font-bold text-gray-800">{offer.clientName[0]}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {/* Status Dropdown */}
                    <select
                      value={offer.status || 'probihajici'}
                      onChange={(e) => handleStatusChange(offer.slug, e.target.value, e)}
                      onClick={(e) => e.stopPropagation()}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer ${getStatusColor(offer.status || 'probihajici')}`}
                    >
                      <option value="probihajici">Probíhající</option>
                      <option value="prijata">Přijatá</option>
                      <option value="odmitnuta">Odmítnutá</option>
                    </select>
                    {/* View Count */}
                    <span className="text-gray-500 text-xs">
                      👁️ {offer.viewCount || 0}x otevřeno
                    </span>
                  </div>
                </div>

                {/* Client Name */}
                <h3 className="text-xl font-bold text-white mb-2">{offer.clientName}</h3>
                <p className="text-gray-400 text-sm mb-4">{offer.eventName}</p>

                {/* Details */}
                <div className="space-y-2">
                  <div className="flex items-center text-gray-300 text-sm">
                    <Calendar className="w-4 h-4 mr-2 text-cyan-400" />
                    <span>{offer.eventDate}{offer.eventTime ? ` • ${offer.eventTime}` : ''}</span>
                  </div>
                  <div className="flex items-center text-gray-300 text-sm">
                    <Users className="w-4 h-4 mr-2 text-cyan-400" />
                    <span>~{offer.guestCount ?? 0} hostů</span>
                  </div>
                </div>

                {/* View Button */}
                <div className="mt-6 pt-4 border-t border-gray-800">
                  <button className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold py-2 px-4 rounded-lg transition-all">
                    Zobrazit nabídku →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
